import { Badge } from '@/components/UI/Badge';
import { ShieldCheck } from 'lucide-react';
import { FC } from 'react';
import { Link } from 'react-router';

import { usePendingApprovalsList } from '../hooks/queries/usePendingApprovalsList';
import { useHasAnySupplierUpdateApprovePermission } from '../hooks/useHasAnySupplierUpdateApprovePermission';

/**
 * Top-level nav-bar Approvals badge (#6096 AC12) — deep-links straight to the Pending
 * Approvals Inbox. Only rendered for users holding any SupplierUpdateApprove* permission,
 * and hidden when the count is 0 or the underlying query errors (no point drawing
 * attention to a badge with nothing actionable, or one we can't trust).
 */
export const PendingApprovalsBadge: FC = () => {
  const { hasAnyApprovePermission, isLoading: isPermissionLoading } =
    useHasAnySupplierUpdateApprovePermission();
  const { data: rows, isError } = usePendingApprovalsList({
    enabled: !isPermissionLoading && hasAnyApprovePermission
  });

  if (isPermissionLoading || !hasAnyApprovePermission || isError) {
    return null;
  }

  const count = rows?.length ?? 0;
  if (count === 0) {
    return null;
  }

  return (
    <Link
      to="/pending-approvals"
      prefetch="render"
      className="hidden items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-white/90 transition-colors duration-150 hover:bg-white/20 hover:text-white sm:flex"
      aria-label={`${count} pending supplier updates awaiting approval`}
    >
      <ShieldCheck className="h-4 w-4" />
      <span className="text-[13px] font-medium">Approvals</span>
      <Badge color="danger" size="sm">
        {count > 99 ? '99+' : count}
      </Badge>
    </Link>
  );
};
